import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from 'lib/utils';
import { ActorCell } from 'components/admin/audit/ActorCell';
import ActionPill from 'components/admin/audit/ActionPill';
import { dayKey, formatTime, importCount, numericDate, technicianImportSummary } from 'utils/auditLabels';
import type { AdminAuditEvent } from 'api/adminAuditApi';
import { GRID_COLS, MUTED_CELL, moduleLabel, type OpenEvent } from './tableColumns';
import { ImportChildRows } from './ImportChildRows';

/** One `technician_import` parent row (design §6.4): expands into `ImportChildRows` on click. */
const ImportRow = ({
  event,
  onOpen,
}: {
  event: AdminAuditEvent;
  onOpen: OpenEvent;
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const fileName = typeof event.context?.fileName === 'string' ? event.context.fileName : '';

  return (
    <>
      {/* The whole row toggles on click; the keyboard and `aria-expanded` live on the button inside,
          since a `row` outside a treegrid may not carry `aria-expanded` (axe aria-conditional-attr). */}
      <div
        role="row"
        onClick={() => setExpanded((v) => !v)}
        className={cn('group grid items-center gap-3 border-b px-4 py-2 cursor-pointer hover:bg-accent focus-within:ring-1 focus-within:ring-ring focus-within:ring-inset', GRID_COLS)}
        style={{ minHeight: 52 }}
      >
        <span role="cell" className={`font-mono text-[12px] ${MUTED_CELL}`}>
          {numericDate(dayKey(event.occurredAt))} {formatTime(event.occurredAt)}
        </span>
        <ActorCell event={event} />
        <span role="cell" className={`truncate text-[13px] ${MUTED_CELL}`}>{moduleLabel(t, event.module)}</span>
        <div role="cell" className="min-w-0">
          <button type="button" aria-expanded={expanded} className="flex w-full min-w-0 items-start gap-1.5 py-1 text-left outline-none">
            <ChevronRight className={cn('mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-90')} aria-hidden="true" />
            <span className="min-w-0">
              {/* Wraps instead of truncating (R-188): the label + counters were clipped at 1440. */}
              <span className="block whitespace-normal break-words text-[13px] font-semibold">{t('Technician import — {{count}} row applied', { count: importCount(event, 'applied') })}</span>
              <span className={`block truncate font-mono text-[11.5px] ${MUTED_CELL}`}>
                {event.entityId} · {fileName}
              </span>
            </span>
          </button>
        </div>
        <div role="cell">
          <ActionPill action={event.action} />
        </div>
        <span role="cell" className={`whitespace-normal break-words text-[13px] ${MUTED_CELL}`}>
          {technicianImportSummary(t, event)}
        </span>
      </div>
      {expanded && <ImportChildRows importId={event.entityId} onOpen={onOpen} />}
    </>
  );
};

export default ImportRow;
